import { useState, useEffect } from 'react'
import { deviceService, companyService, licenseService } from '../services/api'
import {
  Monitor,
  Key,
  Lock,
  Unlock,
  MoreHorizontal,
  ChevronRight
} from 'lucide-react'

const Dashboard = () => {
  const [stats, setStats] = useState({
    devices: { total: 0, active: 0, blocked: 0, unlocked: 0 },
    licenses: { total: 0, active: 0 },
    companies: { total: 0 },
    users: { total: 0 }
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      const [devices, companies, licenses] = await Promise.all([
        deviceService.getAll({ limit: 1000 }),
        companyService.getAll({ limit: 1 }),
        licenseService.getAll({ limit: 1 })
      ])

      const deviceList = devices.data.devices || []
      const blockedCount = deviceList.filter(d => d.status === 'bloqueado').length
      const unlockedCount = deviceList.filter(d => d.status === 'desbloqueado').length

      setStats({
        devices: {
          total: devices.data.pagination.total,
          active: deviceList.filter(d => d.status === 'desbloqueado').length,
          blocked: blockedCount,
          unlocked: unlockedCount
        },
        companies: { total: companies.data.pagination.total },
        licenses: {
          total: licenses.data.pagination.total,
          active: licenses.data.licenses.filter(l => l.status === 'active').length
        },
        users: { total: 0 }
      })
    } catch (error) {
      console.error('Error al cargar estadísticas:', error)
    } finally {
      setLoading(false)
    }
  }

  const blockedPercentage = stats.devices.total > 0 ? (stats.devices.blocked / stats.devices.total * 100).toFixed(1) : 0

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-jcb-500"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Banner Rojo Superior */}
      <div className="bg-jcb-500 px-6 py-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
              <div className="w-8 h-8 bg-jcb-500 rounded-full relative">
                <div className="absolute inset-0 bg-gradient-to-br from-jcb-400 to-jcb-600 rounded-full animate-jcb-pulse"></div>
              </div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">JCB</div>
              <div className="text-sm text-white/80 italic">technology</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-white">Bienvenido de nuevo, JCB</div>
            <div className="text-sm text-white/80">El reporte de todas tus estadísticas está al día</div>
          </div>
        </div>
      </div>

      {/* Tarjetas de Estadísticas */}
      <div className="px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Tarjeta Dispositivos */}
          <div className="bg-white rounded-lg shadow-lg border-l-4 border-jcb-500 p-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-gray-700 font-medium">Dispositivos</h3>
                <div className="text-3xl font-bold text-jcb-500 mt-2">{stats.devices.total}</div>
                <p className="text-sm text-gray-500 mt-1">Total registrados</p>
              </div>
              <div className="w-8 h-8 bg-jcb-500 rounded-full flex items-center justify-center">
                <Monitor className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>

          {/* Tarjeta Códigos */}
          <div className="bg-white rounded-lg shadow-lg border-l-4 border-jcb-500 p-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-gray-700 font-medium">Códigos</h3>
                <div className="text-3xl font-bold text-jcb-500 mt-2">{stats.licenses.total}</div>
                <p className="text-sm text-gray-500 mt-1">Generados</p>
              </div>
              <div className="w-8 h-8 bg-jcb-500 rounded-full flex items-center justify-center">
                <Key className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>

          {/* Tarjeta Bloqueados */}
          <div className="bg-white rounded-lg shadow-lg border-l-4 border-gray-500 p-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-gray-700 font-medium">Bloqueados</h3>
                <div className="text-3xl font-bold text-gray-700 mt-2">{stats.devices.blocked}</div>
                <p className="text-sm text-gray-500 mt-1">En seguridad</p>
              </div>
              <div className="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center">
                <Lock className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>

          {/* Tarjeta Sin Candado */}
          <div className="bg-white rounded-lg shadow-lg border-l-4 border-green-500 p-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-gray-700 font-medium">Sin candado</h3>
                <div className="text-3xl font-bold text-green-500 mt-2">{stats.devices.unlocked}</div>
                <p className="text-sm text-gray-500 mt-1">Disponibles</p>
              </div>
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <Unlock className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Secciones Inferiores */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Estado de Dispositivos */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-gray-700 font-medium">Estado de dispositivos</h3>
              <MoreHorizontal className="w-5 h-5 text-gray-400" />
            </div>
            <div className="flex items-center justify-center">
              <div className="relative">
                {/* Gráfico de Donut */}
                <div className="w-32 h-32 rounded-full border-8 border-gray-200 flex items-center justify-center">
                  <div
                    className="absolute inset-0 rounded-full border-8 border-jcb-500"
                    style={{
                      clipPath: `polygon(50% 50%, 50% 0%, ${50 + (blockedPercentage * 3.6)}% 0%, ${50 + (blockedPercentage * 3.6)}% 50%)`
                    }}
                  ></div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-jcb-500">{blockedPercentage}%</div>
                    <div className="text-sm text-gray-500">Bloqueados</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Estadísticas */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-gray-700 font-medium">Estadísticas</h3>
              <MoreHorizontal className="w-5 h-5 text-gray-400" />
            </div>
            <div className="flex items-end justify-center space-x-8 h-32">
              {/* Barra Dispositivos */}
              <div className="flex flex-col items-center">
                <div
                  className="w-8 bg-jcb-500 rounded-t"
                  style={{ height: `${(stats.devices.total / Math.max(stats.devices.total, stats.licenses.total)) * 80}px` }}
                ></div>
                <div className="text-xs text-gray-500 mt-2 text-center">
                  {stats.devices.total}<br />Dispositivos
                </div>
              </div>

              {/* Barra Códigos */}
              <div className="flex flex-col items-center">
                <div
                  className="w-8 bg-jcb-500 rounded-t"
                  style={{ height: `${(stats.licenses.total / Math.max(stats.devices.total, stats.licenses.total)) * 80}px` }}
                ></div>
                <div className="text-xs text-gray-500 mt-2 text-center">
                  {stats.licenses.total}<br />Códigos
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard 